import React from "react";
import { Stack, styled, Typography } from "@mui/material";
import { TotalSummary } from "@/pages/users/data";

import { useSearchParams } from "react-router-dom";
import { useUsers } from "hooks/use-users";
import { DataTable } from "@/components/custom/data-table";
import { UserColumns } from "@/pages/users/customers/_components/user-column";
import CustomSearchInput from "@/components/custom/custom-search-input";
import CustomSelectInput from "@/components/custom/custom-select-input";
import { useUserStats } from "hooks/use-user-stats";
import CustomPagination from "@/components/custom/custom-pagination";
import CustomSimpleCard from "@/components/custom/custom-simple-card";
import { CustomButton } from "@/components/custom/custom-button";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useExportCsv } from "@/hooks/use-export-csv";
import { getUsers } from "@/services/users.service";

const SectionRoot = styled("section")(({ theme }) => ({
  margin: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const Card = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: "16px 20px",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const Customers = () => {
  const [searchParams] = useSearchParams();
  const searchValue = searchParams.get("search") || "";

  const currentPage = Number(searchParams.get("page") || 0);
  const pageSize = Number(searchParams.get("size") || 10);
  const corporate = searchParams.get("corporate");
  const businessType = searchParams.get("businessType");
  const isActive = searchParams.get("isActive");

  const queryParams = {
    page: currentPage,
    size: pageSize,
    corporate: corporate ? corporate : undefined,
    businessType: businessType ? businessType : undefined,
    isActive: isActive ? isActive : undefined,
    search: searchValue,
  };

  const {
    data: users,
    isLoading: isUsersLoading,
    isError,
  } = useUsers(true, queryParams);
  const { isLoading: isStatsLoading, ...stats } = useUserStats();

  const { exportToCsv, isExporting } = useExportCsv({
    fetchFn: getUsers,
    params: queryParams,
    filename: "customers",
    transform: (res) =>
      res?.data?.map((user) => ({
        "First Name": user.firstName,
        Surname: user.surname,
        Email: user.email,
        "Phone Number": user.phoneNumber,
        "Date of Birth": user.dateOfBirth,
        Gender: user.gender,
        Type: user.corporate ? "Corporate" : "Individual",
        Status: user.active ? "Active" : "Inactive",
        "Organisation Name": user.otherDetails?.organisationName ?? "",
        "Organisation Email": user.otherDetails?.organisationEmail ?? "",
        "Organisation Phone": user.otherDetails?.organisationPhone ?? "",
        "Business Type": user.otherDetails?.businessType ?? "",
        City: user.otherDetails?.organizationCity ?? "",
        State: user.otherDetails?.organizationState ?? "",
        "Created At": user.createdAt,
      })) ?? [],
  });

  return (
    <SectionRoot>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {TotalSummary.map(({ title, objKeyRepr, icon }, idx) => (
          <CustomSimpleCard
            key={`${objKeyRepr}-${idx}`}
            title={title}
            icon={icon}
            isLoading={isStatsLoading}
          >
            <Typography variant="h6">{stats?.[objKeyRepr] ?? 0}</Typography>
          </CustomSimpleCard>
        ))}
      </Stack>

      <Card>
        <Stack
          direction={"row"}
          spacing={3}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction={"row"} spacing={3}>
            <CustomSearchInput
              paramKey="search"
              placeholder="Search users..."
            />
            <CustomSelectInput
              paramKey="businessType"
              label="Business Type"
              placeholder="All Business Types"
              options={[
                { label: "Business", value: "business" },
                { label: "Finance", value: "finance" },
              ]}
            />
            <CustomSelectInput
              paramKey="isActive"
              label="Status"
              placeholder="All Statuses"
              options={[
                { label: "Active", value: "true" },
                { label: "Inactive", value: "false" },
              ]}
            />
            <CustomSelectInput
              paramKey="corporate"
              label="Type"
              placeholder="All Types"
              options={[
                { label: "Corporate", value: "true" },
                { label: "Individual", value: "false" },
              ]}
            />
          </Stack>

          <CustomButton
            variantType="outline"
            endIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            onClick={exportToCsv}
            isLoading={isExporting}
            sx={{ height: 35 }}
          >
            {isExporting ? "Exporting..." : "Export"}
          </CustomButton>
        </Stack>

        <DataTable
          columns={UserColumns}
          data={users?.data ?? []}
          isLoading={isUsersLoading}
          isError={isError}
        />
        <CustomPagination
          total={users?.total_count ?? 0}
          perPageOptions={[5, 10, 20, 50, 100]}
        />
      </Card>
    </SectionRoot>
  );
};

export default Customers;
